import { useState } from 'react';
import { formatWeldStartEndRange } from '../../../../../lib/formatUtils';
import { getWeldStatusCalculationMethod } from '../../../../../utils/statusUtils';
import { WeldPassList } from '../../../../../components/WeldPassList';
import { weldDurationDisplay } from '../../../utils/weldDurationDisplay';
import { outcomeLabel } from '../../../utils/weldDrillDownModel';
import type { PipeOption } from '../../../../ndt/components/FilterPanel';
import {
  formatDrillDownPipeSizeLabel,
  formatStatusMethodLabel,
  formatWpsRawStatusDisplay,
} from '../../../utils/passViewFormatters';
import { glossaryText } from '../../../utils/weldFieldGlossary';
import type { PassViewRecord } from '../../../utils/fetchWeldViewApis';
import { ColumnHeaderHelp } from '../../shared/ColumnHeaderHelp';
import type { WpsViolationRecord } from '../../../utils/wpsViolationModel';
import type { WeldDrillDownRow } from './drillDownTypes';
import { NdtIndicationSummary } from './NdtIndicationSummary';
import { ProductionPassSummaryTable } from './ProductionPassSummaryTable';
import { WpsViolationSummaryPanel } from './WpsViolationSummaryPanel';
import { LiveScoringPanel } from './LiveScoringPanel';
import { mapDrillDownRowToWeldData } from './mapDrillDownRowToWeldData';
import type { NdtIndicationRow } from './drillDownTypes';

interface WeldDrillDownDetailProps {
  row: WeldDrillDownRow;
  statusCalculationMethod: string;
  ndtLogs: NdtIndicationRow[];
  ndtLoading?: boolean;
  ndtError?: string | null;
  onEditNdt?: () => void;
  canEditNdt?: boolean;
  welderIds?: string;
  passesLoading?: boolean;
  passRecords?: PassViewRecord[];
  wpsViolations?: WpsViolationRecord[];
  wpsViolationsLoading?: boolean;
  showWpsViolationDetail?: boolean;
  /** False when no WPS limits row matches this weld's job. */
  limitsConfigured?: boolean;
  pipeId?: string | null;
  pipeOptions?: PipeOption[];
  dashboardProjectId?: number | null;
}

function DetailField({
  label,
  value,
  hint,
  helpKey,
}: {
  label: string;
  value: string;
  hint?: string | null;
  helpKey?: string;
}) {
  return (
    <div className="rounded-lg bg-[rgba(0,39,63,0.03)] px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">
        {helpKey ? (
          <ColumnHeaderHelp label={label} help={glossaryText(helpKey)} />
        ) : (
          label
        )}
      </p>
      <p className="mt-0.5 text-sm font-medium text-blueprint-blue">{value}</p>
      {hint ? <p className="mt-0.5 text-xs font-normal text-heritage-grey">{hint}</p> : null}
    </div>
  );
}

export const WeldDrillDownDetail = ({
  row,
  statusCalculationMethod,
  ndtLogs,
  ndtLoading,
  ndtError,
  onEditNdt,
  canEditNdt = false,
  welderIds = '—',
  passesLoading = false,
  passRecords = [],
  wpsViolations = [],
  wpsViolationsLoading = false,
  showWpsViolationDetail = false,
  limitsConfigured = true,
  pipeId = null,
  pipeOptions = [],
  dashboardProjectId = null,
}: WeldDrillDownDetailProps) => {
  const [passListOpen, setPassListOpen] = useState(false);

  const activeStatus = row.productionRow
    ? getWeldStatusCalculationMethod(statusCalculationMethod, row.productionRow)
    : null;

  const timeRange = formatWeldStartEndRange(row.startTimeMs, row.endTimeMs);
  const startEndDisplay =
    timeRange.timeLine !== '—' ? `${timeRange.dateLine} · ${timeRange.timeLine}` : timeRange.dateLine;
  const duration = weldDurationDisplay(row);

  const pipeIdForRow =
    row.productionRow?.pipeId != null
      ? String(row.productionRow.pipeId)
      : row.productionRow?.pipe_details_id != null
        ? String(row.productionRow.pipe_details_id)
        : pipeId;
  const pipeDisplayFallback = pipeOptions.find(
    (p) => String(p.pipeId) === pipeIdForRow,
  )?.displayName;

  const hasCachedViolations = wpsViolations.length > 0;
  const showWpsLoading = wpsViolationsLoading && !hasCachedViolations;
  const [passSummaryOpen, setPassSummaryOpen] = useState(false);

  return (
    <>
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
        <div className="space-y-8 overflow-y-auto p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-heading text-base font-semibold text-blueprint-blue">
              Weld #{row.weldNumber}
            </h3>
            <span className="text-xs font-medium text-heritage-grey">{outcomeLabel(row.outcome)}</span>
          </div>

          <section>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-heritage-grey">
                Production
              </h4>
              {/* <button
                type="button"
                onClick={() => setPassListOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-readable-red hover:underline"
              >
                Open full pass detail
                <ExternalLink className="h-3 w-3" />
              </button> */}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <DetailField label="Job" value={row.jobNumber || '—'} />
              <DetailField
                label="Weld Duration"
                value={duration.label}
                hint={duration.clockSpanLabel}
                helpKey="drilldown_duration"
              />
              <DetailField label="Start → End" value={startEndDisplay} helpKey="drilldown_start_end" />
              <DetailField
                label="Welder ID(s)"
                value={passesLoading ? 'Loading…' : welderIds}
              />
              <DetailField
                label="Pipe"
                value={formatDrillDownPipeSizeLabel(row, pipeDisplayFallback)}
                helpKey="drilldown_pipe"
              />
            </div>

            <div className="mt-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">
                Pass summary
              </p>
              <ProductionPassSummaryTable
                passes={passRecords}
                loading={passesLoading}
                statusCalculationMethod={statusCalculationMethod}
                outOfLimitContext={showWpsViolationDetail}
                violations={wpsViolations}
                limitsConfigured={limitsConfigured}
                weldNumber={row.weldNumber}
                jobNumber={row.jobNumber}
                open={passSummaryOpen}
                onOpenChange={setPassSummaryOpen}
              />
            </div>
          </section>

          {showWpsViolationDetail && (
            <WpsViolationSummaryPanel
              violations={wpsViolations}
              loading={showWpsLoading}
              limitsConfigured={limitsConfigured}
              checkedUsingLabel={formatStatusMethodLabel(statusCalculationMethod)}
              teaser
              onOpenPassSummary={() => setPassSummaryOpen(true)}
            />
          )}

          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-heritage-grey">
              WPS status
            </h4>
            <div className="grid gap-2 sm:grid-cols-2">
              <DetailField
                label="Overall status"
                value={formatWpsRawStatusDisplay(activeStatus != null ? String(activeStatus) : null)}
                helpKey="wps_status_active"
              />
              <div className="rounded-lg bg-[rgba(0,39,63,0.03)] px-3 py-2 sm:col-span-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">
                  Checked using
                </p>
                <p className="mt-0.5 text-sm font-medium text-blueprint-blue">
                  {formatStatusMethodLabel(statusCalculationMethod)}
                </p>
              </div>
              <DetailField
                label="Instant reading"
                value={formatWpsRawStatusDisplay(row.statusByInstantaneous)}
                helpKey="wps_status_instantaneous"
              />
              <DetailField
                label="By zone average"
                value={formatWpsRawStatusDisplay(row.statusByAvgZone)}
                helpKey="wps_status_avg_zone"
              />
              <DetailField
                label="By pass average"
                value={formatWpsRawStatusDisplay(row.statusByAvgPass)}
                helpKey="wps_status_avg_pass"
              />
              <DetailField
                label="By tilt average"
                value={formatWpsRawStatusDisplay(row.statusByAvgTilt)}
                helpKey="wps_status_avg_tilt"
              />
            </div>
          </section>

          <LiveScoringPanel
            row={row}
            passRecords={passRecords}
            weldingSystem={row.productionRow?.weldingSystem as string | undefined}
            dashboardProjectId={dashboardProjectId}
          />

          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-heritage-grey">
                NDT indications
              </h4>
              {canEditNdt && onEditNdt && (
                <button
                  type="button"
                  onClick={onEditNdt}
                  className="text-xs font-semibold text-readable-red hover:underline"
                >
                  Edit NDT log
                </button>
              )}
            </div>
            <NdtIndicationSummary
              logs={ndtLogs}
              loading={ndtLoading}
              error={ndtError}
              weldNumber={row.weldNumber}
              jobNumber={row.jobNumber}
            />
          </section>
        </div>
      </div>

      {passListOpen && (
        <WeldPassList
          isOpen={passListOpen}
          onClose={() => setPassListOpen(false)}
          weld={mapDrillDownRowToWeldData(row, pipeId, pipeOptions)}
          assemblyNumber="Main"
          statusCalculationMethod={statusCalculationMethod}
          passViewStatusMethod={statusCalculationMethod}
          zoneViewStatusMethod={statusCalculationMethod}
          tiltViewStatusMethod={statusCalculationMethod}
          canEdit={canEditNdt}
        />
      )}
    </>
  );
};
